/* eslint-disable import/no-cycle */
import AuthStore from 'auth-store';
import { debounce } from 'lodash';
import { persistStorage } from 'helper';
import { showDrawer } from 'partial/components/Modal';
import { req, useApiGet, useApiLoading } from 'react-reqq-lite';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getUserTypeFromToken } from './transformers';
import OverrideModal from './modals/OverrideModal';
import * as actions from './actions';
import * as c from './constants';

export const useAuth = () => {
  const auth = useApiGet(c.AUTHENTICATION, {});
  useEffect(() => {
    const token = AuthStore.get('_token');
    req.set(c.AUTHENTICATION, {
      isAuthenticated: !!token,
      userType: getUserTypeFromToken(token),
    });
  }, []);
  return [typeof auth.isAuthenticated === 'boolean', auth.isAuthenticated];
};

export const withOverride = (func) => {
  showDrawer({
    title: 'Override',
    className: 'modal-sm',
    content: (close) => <OverrideModal onSubmit={func} onClose={close} />,
  });
};

export const useLoginViaSso = () => {
  const isLoading = useApiLoading(c.LOGIN_SSO, 'get');
  const submit = useCallback(() => {
    const token = '-temp-';
    AuthStore.set('_token', token);
    req.set(c.AUTHENTICATION, {
      isAuthenticated: true,
      userType: getUserTypeFromToken(token),
    });
  }, []);
  return [isLoading, submit];
};

export const useLogin = () => {
  const isLoading = useApiLoading(c.LOGIN, 'post');
  return [isLoading, actions.login];
};

export const useRegister = () => {
  const isLoading = useApiLoading(c.REGISTER, 'post');
  return [isLoading, actions.register];
};

export const useResendEmail = (id, onSuccess, onError) => {
  const isLoading = useApiLoading(c.RESEND_EMAIL_VERIFICATION, 'post');
  const resend = useCallback(() => {
    if (id) {
      actions.resendEmailVerification(id, onSuccess, onError);
    }
  }, [id, onSuccess, onError]);
  return [isLoading, resend];
};

const usePersistState = (key, defaultState = null) => {
  const [state, setState] = useState(persistStorage.get(key, defaultState));
  // eslint-disable-next-line
  const debounceSet = useCallback(
    debounce((newValue) => {
      persistStorage.set(key, newValue);
    }, 500),
    [key]
  );
  const set = useCallback(
    (newValue) => {
      persistStorage.set(key, newValue);
    },
    [key]
  );
  const stateRef = useRef();
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  const handleSet = useCallback(
    (v) => {
      const newState = typeof v === 'function' ? v(stateRef.current) : v;
      setState(newState);
      debounceSet(newState);
    },
    [setState, debounceSet]
  );
  return [state, handleSet, set];
};

/* PROFILE START */
export const useGetProfile = () => {
  const [profile, debounceSet] = usePersistState('profile', null);
  useEffect(() => {
    actions.getProfile(debounceSet);
  }, [debounceSet]);
  const get = useCallback(() => {
    actions.getProfile(debounceSet);
  }, [debounceSet]);
  return [profile, get];
};

export const useProfile = () => {
  const contx = useContext(c.ProfileContext);
  return [contx?.profile, contx?.getProfile];
};

export const useChangePassword = () => {
  const isLoading = useApiLoading(c.CHANGE_PASSWORD, 'post');
  return [actions.changePassword, isLoading];
};

export const useSwitchProfile = () => {
  const isLoading = useApiLoading(c.PROFILE, 'post');
  return [isLoading, actions.switchProfile];
};
/* PROFILE END */

/* MY ACCOUNT START */

export const useCreateAccount = () => {
  const isLoading = useApiLoading(c.CREATE_ACCOUNT, 'post');
  return [isLoading, actions.createAccount];
};

export const useCurrentAccount = () => {
  const isLoading = useApiLoading(c.CURRENT_ACCOUNT, 'get');
  const data = useApiGet(c.CURRENT_ACCOUNT, {});
  useEffect(() => {
    actions.getCurrentAccount();
  }, []);
  return [isLoading, data];
};

export const useUpdateAccount = () => {
  const isLoading = useApiLoading(c.CURRENT_ACCOUNT, 'put');
  return [isLoading, actions.updateAccount];
};

export const useActiveAccount = () => {
  const data = useApiGet(c.CURRENT_ACCOUNT, {});
  return [data];
};

export const useToggleAccount = () => {
  const isLoading = useApiLoading(c.TOGGLE_MODE, 'put');
  const [activeAccount] = useActiveAccount();
  const submit = useCallback(
    (payload, onSuccess, onError) => {
      actions.toggleAccount(activeAccount.id, payload, onSuccess, onError);
    },
    [activeAccount]
  );
  return [isLoading, submit];
};

/* MY ACCOUNT END */

/* FORGOT PASSWORD START */
export const useForgotPassword = (
  options = {
    onSendInstructions: () => {},
    onSetNewPassword: () => {},
  }
) => {
  const isLoadingSendEmail = useApiLoading(c.FORGOT_PASSWORD_SEND, 'post');
  const isLoadingSetPassword = useApiLoading(c.FORGOT_PASSWORD_SET, 'post');

  // eslint-disable-next-line
  const sendEmailInstruction = useCallback(
    actions.forgotPasswordSendEmailInstruction(options?.onSendInstructions),
    [options]
  );

  // eslint-disable-next-line
  const setNewPassword = useCallback(
    actions.forgotPasswordSetNewPassword(options?.onSetNewPassword),
    [options]
  );

  return {
    sendEmailInstruction,
    setNewPassword,
    isLoadingSendEmail,
    isLoadingSetPassword,
  };
};

export const useValidateForgotPassword = () => {
  const isLoading = useApiLoading(c.VALIDATE_FORGOT_PASSWORD, 'post');
  return [actions.validateForgotPassword, isLoading];
};
/* FORGOT PASSWORD END */
